/**
 * index.js — /country-flags controller.
 *
 * Owns: the flag catalogue and picker, the live preview loop, and the export
 * pipeline. The physics is cloth.js, the air is wind.js, the look is
 * fabric.js + scene.js, the file is export.js — this file only decides WHEN.
 *
 * Preview and export are deliberately different budgets of the same scene
 * (see export.js). The preview adapts its grid to whatever the device holds;
 * the export runs a denser grid offline, so the file does not inherit the
 * viewer's frame drops.
 */

import { Cloth, FABRIC, extensionAt, liftOffSpeed } from './cloth.js';
import { Wind, BEAUFORT, beaufortFor, windUnits } from './wind.js';
import { loadFlag, maskSampler, renderStill, palette, alphaGrid } from './flagtex.js';
import { FlagScene, BACKGROUNDS, LIGHTING, CAMERAS } from './scene.js';
import {
    simulate, encodeVideo, encodePngSequence, encodeGif,
    RESOLUTIONS, ASPECTS, outputSize, gridFor,
} from './export.js';

/* Where the engine comes from is a DEPLOYMENT decision, not a constant. The
   page resolves it with cdn_chain() — own origin first on every locale, mirror
   second — and window.__assetChain walks the list, so a blocked or slow CDN
   costs a retry rather than the whole tool. A hardcoded path here bypassed all
   of that. */
const THREE_URLS = Array.isArray(window.__CF_THREE) && window.__CF_THREE.length
    ? window.__CF_THREE
    : ['/vendor/threejs/160/three.module.min.js'];
const SLUG = 'country-flags';

const $ = id => document.getElementById(id);
const t = (k, fb) => (window.__CF_I18N && window.__CF_I18N[k]) || fb || k;

/* Flag geometry in metres. 1.5 × 1.0 is a common outdoor size, and because
   the solver works in SI these numbers are load-bearing, not decoration: they
   set the mass that gravity acts on and the area the wind pushes. Wider flags
   keep the height and grow — a 28:11 Qatari flag really is a long thin sheet
   and really does behave differently from a square Swiss one. */
const FLAG_HEIGHT_M = 1.0;

/* Real outdoor flag heights, in metres. This is NOT a zoom: the solver works
   in SI, so the size changes what the cloth DOES — a 3 m flag carries more
   weight per fold and its waves are correspondingly slower and longer, while a
   0.6 m hand flag flutters. Framing follows automatically, so the picture size
   stays put and only the motion changes, which is the honest way round. */
const FLAG_SIZES = {
    hand: 0.45,     // hand flag / car flag
    small: 0.9,     // balcony
    normal: 1.35,   // ordinary flagpole
    large: 2.0,     // civic building
    huge: 3.2,      // stadium / monument
};

const state = {
    code: 'ru',
    aspect: 1.5,
    kind: 'country',
    shape: 'rect',
    size: 'normal',
    zoom: 1,
    beaufort: 4,
    windDir: 8,
    gustiness: 1,
    fabric: 'polyester',
    wear: 0,
    pin: 'halyard',
    ceremony: 'none',
    customImage: null,
    background: 'sky',
    lighting: 'noon',
    camera: 'hero',
    pole: 'metal',
    finial: 'ball',
    groundShadow: false,
    resolution: '1080p',
    outAspect: '16:9',
    duration: 10,
    fps: 30,
    format: 'mp4',
    quality: 'standard',
    seed: 7,
};

let THREE = null;
let manifest = null;
let names = {};
let scene = null;
let cloth = null;
let wind = null;
let flagTex = null;
let rafId = 0;
let running = false;
let busy = false;
let lastT = 0;
let frameCost = 16;

/* ───────────────────────────── telemetry ─────────────────────────────
   Five stages, in the order a user actually passes them:

     ready      the engine is up and the catalogue is on screen
     picked     a flag was chosen — the first act of intent
     tuned      wind/fabric/scene/ceremony touched — real engagement
     exporting  a file was asked for
     complete   a file was delivered  (fired by __aeToolComplete)

   window.__stage adds dwell_ms per stage and a final_stage when the visitor
   leaves, so the interesting question — where do people stop — is answerable
   without adding an "abandoned" event that nobody would fire.

   ⚠️ Stages only move FORWARD. Re-firing `picked` every time someone tries
   another flag would bury the funnel in noise and make dwell meaningless. */
const STAGES = ['ready', 'picked', 'tuned', 'exporting', 'complete'];
let stageAt = -1;
function stage(name, meta) {
    const i = STAGES.indexOf(name);
    if (i < 0 || i <= stageAt) return;
    stageAt = i;
    window.__stage?.(name, meta || {});
}

/* One shape for "what was this made of", attached to the funnel events that
   matter. Without it the numbers say how many exports happened and nothing
   about what people actually reach for. */
function shot() {
    const f = manifest?.byCode?.[state.code];
    return {
        code: state.customImage ? 'custom' : state.code,
        kind: state.customImage ? 'custom' : (f?.k || 'country'),
        ar: f ? +f.ar.toFixed(2) : null,
        bft: state.beaufort,
        fabric: state.fabric,
        size: state.size,
        bg: state.background,
        light: state.lighting,
        cam: state.camera,
        ceremony: state.ceremony,
        res: state.resolution,
        aspect: state.outAspect,
        dur: state.duration,
        fps: state.fps,
        fmt: state.format,
        quality: state.quality,
    };
}

// ───────────────────────────── catalogue ─────────────────────────────

async function loadManifest() {
    const r = await fetch('/data/flags/manifest.json', { cache: 'force-cache' });
    if (!r.ok) throw new Error('manifest-unavailable');
    manifest = await r.json();
    manifest.byCode = Object.fromEntries(manifest.flags.map(f => [f.c, f]));
}

/** Localised label for a catalogue entry. */
function labelFor(f) {
    if (f.k === 'custom') return f.name || t('custom.title', 'Своя картинка');
    if (f.k === 'country') return names[f.c.toUpperCase()] || f.c.toUpperCase();
    return t('flag.' + f.c, f.c);
}

/**
 * Human ratio string — the tool's whole differentiator, so it is always shown.
 *
 * A decimal here is a statement, not a fallback: three flags in the set really
 * do not have a fractional proportion. Togo's is the golden ratio itself (the
 * only national flag defined that way), Nepal's comes out of a geometric
 * construction, and the Roman vexilloid never had a codified one. Rounding
 * any of them to a neat fraction would be inventing a fact — see
 * RATIO_OVERRIDE in scripts/build-flag-data.mjs.
 */
function ratioLabel(f) {
    if (!f) return '';
    if (f.shape === 'pennon') return t('ratio.pennon', 'непрямоугольный');
    if (f.r) return `${f.r[0]}:${f.r[1]}`;
    if (f.note === 'golden') return 'φ : 1';
    return f.ar.toFixed(3) + ' : 1';
}

/** Longer form for the facts panel, where there is room to say why. */
function ratioDetail(f) {
    if (!f) return '';
    if (f.note === 'golden') return t('ratio.golden', 'золотое сечение');
    if (f.shape === 'pennon') return t('ratio.pennon_note', '');
    if (!f.r) return t('ratio.irrational', '');
    return '';
}

/* ── The colour vocabulary ────────────────────────────────────────────────
   Ten buckets, the same ones scripts/build-flag-palettes.mjs assigns, in the
   order people reach for them. The swatch shows a real flag colour rather than
   a CSS keyword: `red` in a browser is #ff0000, which no flag on earth uses. */
const COLOR_SWATCH = {
    red: '#d52b1e', blue: '#0039a6', green: '#009b3a', yellow: '#fedf00',
    white: '#ffffff', black: '#1a1a1a', orange: '#ff9933', brown: '#8b5a2b',
    purple: '#6b3fa0',
};
const COLOR_ORDER = ['red', 'blue', 'green', 'yellow', 'white', 'black', 'orange', 'brown', 'purple'];

const filters = { colors: new Set(), layout: null };

/** Chips are built from the catalogue, so a colour nothing has never appears. */
function buildColorChips() {
    const box = $('cfColors');
    if (!box || !manifest) return;
    const tally = new Map();
    for (const f of manifest.flags) for (const c of (f.cl || [])) tally.set(c, (tally.get(c) || 0) + 1);
    box.innerHTML = COLOR_ORDER.filter(c => tally.get(c)).map(c =>
        `<button type="button" class="cf-cchip" data-cf-color="${c}" aria-pressed="false">` +
        `<i style="background:${COLOR_SWATCH[c]}"></i>` +
        `<span>${escapeHtml(t('color.' + c, c))}</span>` +
        `<span class="cf-cn">${tally.get(c)}</span></button>`).join('');
}

/**
 * Band chips carry their counts for the same reason the colour chips do — and
 * here it matters more: 223 of 279 flags have no bands at all, so «Горизонтальные»
 * is a narrow slice, not a way to browse. Farid hit exactly this: with the chip
 * on, Turkey and Cyprus were «missing».
 */
function buildLayoutChips() {
    const box = $('cfLayouts');
    if (!box || !manifest) return;
    const tally = manifest.flags.reduce((m, f) => (m[f.lay] = (m[f.lay] || 0) + 1, m), {});
    box.innerHTML = ['h', 'v'].map(k =>
        `<button type="button" class="cf-lchip" data-cf-layout="${k}" aria-pressed="false">` +
        `${escapeHtml(t('layout.' + k, k))}<span class="cf-cn">${tally[k] || 0}</span></button>`
    ).join('');
}

/** Search, region and band direction — the constraints that are not colours. */
function matchesBase(f, q, region, ignoreLayout) {
    if (region === 'org') { if (f.k !== 'org') return false; }
    else if (region === 'historical') { if (f.k !== 'historical') return false; }
    else if (region !== 'all') { if (f.k !== 'country' || !f.rg?.includes(region)) return false; }
    if (!ignoreLayout && filters.layout && f.lay !== filters.layout) return false;
    if (!q) return true;
    return labelFor(f).toLowerCase().includes(q) || f.c.toLowerCase().includes(q);
}

function sortByName(items) {
    const coll = new Intl.Collator(window.__LOCALE || 'en');
    return items.sort((a, b) => coll.compare(labelFor(a), labelFor(b)));
}

/**
 * One flag on the wall.
 *
 * ⚠️ THE WIDTH IS THE FLAG'S OWN. Every catalogue in this niche — flagpedia,
 * countryflags, every icon set — draws flags into one uniform box, which is
 * where the idea that all flags are the same shape comes from. We derive each
 * ratio from the flag's own viewBox, so the wall can show it: same height,
 * true width. That is the tool's central claim, rendered instead of asserted.
 */
function flagButton(f, selected) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'cf-flag' + (f.c === selected ? ' is-on' : '');
    b.dataset.code = f.c;
    b.style.setProperty('--ar', String(f.ar));
    b.setAttribute('aria-pressed', f.c === selected ? 'true' : 'false');
    b.innerHTML =
        `<span class="cf-flag-img"><img src="/data/flags/svg/${f.c}.svg" alt="" loading="lazy" decoding="async"></span>` +
        `<span class="cf-flag-name">${escapeHtml(labelFor(f))}</span>` +
        `<span class="cf-flag-ratio">${escapeHtml(ratioLabel(f))}</span>`;
    return b;
}

function renderWall(el, items) {
    if (!el) return;
    el.innerHTML = '';
    const frag = document.createDocumentFragment();
    for (const f of items) frag.appendChild(flagButton(f, state.code));
    el.appendChild(frag);
}

/**
 * The finder.
 *
 * With colours chosen it answers in TWO groups instead of asking the visitor
 * to pick a matching mode. «Бело-красный флаг какой страны» means Poland,
 * Indonesia, Monaco — flags that are red and white and nothing else — so the
 * exact set leads; the flags that merely CONTAIN those colours (Russia, France)
 * follow underneath. A single list with either rule alone is wrong half the
 * time, and a mode switch makes the visitor state which question they meant.
 */
function buildPicker() {
    const q = ($('cfSearch')?.value || '').trim().toLowerCase();
    const region = $('cfRegion')?.value || 'all';
    /* base = что нашлось по ИМЕНИ и региону, до цветов и полос: именно эта
       величина отвечает на вопрос «а флаг-то вообще есть?». */
    const base = manifest.flags.filter(f => matchesBase(f, q, region, true));
    const sel = [...filters.colors];

    const byLayout = filters.layout ? base.filter(f => f.lay === filters.layout) : base;
    let exact = byLayout, wider = [];
    if (sel.length) {
        exact = byLayout.filter(f => f.cl && f.cl.length === sel.length && sel.every(c => f.cl.includes(c)));
        wider = byLayout.filter(f => f.cl && f.cl.length > sel.length && sel.every(c => f.cl.includes(c)));
    }
    sortByName(exact); sortByName(wider);

    renderWall($('cfGrid'), exact);
    renderWall($('cfGridAlso'), wider);

    const label = $('cfExactLabel');
    if (label) label.textContent = sel.length ? t('group.exact', 'Ровно эти цвета') : t('group.all', 'Все флаги');
    const count = $('cfCount');
    if (count) count.textContent = String(exact.length);
    const alsoCount = $('cfAlsoCount');
    if (alsoCount) alsoCount.textContent = String(wider.length);
    const alsoWrap = $('cfAlsoWrap');
    if (alsoWrap) alsoWrap.hidden = wider.length === 0;
    const exactWrap = $('cfExactWrap');
    if (exactWrap) exactWrap.hidden = exact.length === 0 && wider.length > 0;
    /* ⚠️ ПУСТО — ЭТО НЕ ОТВЕТ. «Почему не все флаги есть?» — потому что при
       включённом чипе «Горизонтальные» Турции и Кипра не существует: у них
       раскладка «другая», как у 223 флагов из 279. Каталог был прав, молчала
       страница. Теперь она считает, сколько нашлось бы ПО ИМЕНИ без фильтров,
       и если разница есть — говорит об этом и предлагает сброс. */
    renderEmpty(base.length, exact.length + wider.length, q);

    const reset = $('cfReset');
    if (reset) reset.hidden = !sel.length && !filters.layout;
    document.querySelectorAll('[data-cf-color]').forEach(b => {
        const on = filters.colors.has(b.dataset.cfColor);
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-cf-layout]').forEach(b => {
        const on = filters.layout === b.dataset.cfLayout;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
}

/** Selecting a flag from any wall. */
async function pickFlag(code, from) {
    if (!code || !manifest.byCode[code]) return;
    state.code = code;
    state.customImage = null;
    history.replaceState(null, '', updateQuery('c', state.code));
    renderCard(manifest.byCode[code]);          // instant: the answer must not wait on WebGL
    buildPicker();
    if (scene) { await applyFlag(); resizePreview(); }
    stage('picked', { code, kind: manifest.byCode[code]?.k });
    window.__aeFunnel?.('flag_picked', { code, kind: manifest.byCode[code]?.k, from });
}

/** Why nothing is showing, in words, with the way out. */
function renderEmpty(byNameAndRegion, shown, q) {
    const el = $('cfEmpty');
    if (!el) return;
    if (shown > 0) { el.hidden = true; el.innerHTML = ''; return; }
    el.hidden = false;

    const filtered = filters.colors.size || filters.layout;
    if (byNameAndRegion > 0 && filtered) {
        const msg = t('empty.filtered', 'Найдено :n, но ни один не проходит по выбранным фильтрам.')
            .replace(':n', String(byNameAndRegion))
            .replace(':q', q || '');
        el.innerHTML = `<div>${escapeHtml(msg)}</div>` +
            `<button type="button" class="lg-btn lg-btn-ghost lg-btn-sm" data-cf-clear>` +
            `${escapeHtml(t('reset', 'Сбросить фильтры'))}</button>`;
    } else {
        el.textContent = t('nothing_found', 'Ничего не нашлось.');
    }
}

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// ───────────────────────────── simulation setup ─────────────────────────────

function fabricCfg() { return FABRIC[state.fabric] || FABRIC.polyester; }
function flagH() { return FLAG_SIZES[state.size] ?? FLAG_SIZES.normal; }

function buildCloth(mode) {
    const f = fabricCfg();
    const { nx, ny } = gridFor(mode, state.aspect, state.quality);
    const h = flagH();
    const c = new Cloth({
        width: h * state.aspect,
        height: h,
        nx, ny,
        gsm: f.gsm,
        stiffness: f.stiffness,
        bend: f.bend,
        damping: f.damping,
    });
    c.pin(state.pin);
    if (flagTex?.mask) {
        const s = maskSampler(flagTex.mask);
        c.setMask((u, v) => (s(u, v) > 0.5 ? 1 : 0));
    }
    return c;
}

/* ─────────────────────────── Ceremony ───────────────────────────
   Three things a flag DOES that have nothing to do with wind, and that no
   other flag generator offers at all:

     half_mast — a static position, and a specific one: the tradition is the
                 flag's own height below the truck, not "somewhere lower".
     raise     — hoisted from the foot of the pole to the top
     lower     — the reverse

   All three are the same thing to the solver — the attachment points move —
   which is why cloth.js exposes hoistAnchor() rather than a special mode. The
   sheet's response (it lags, swings, and settles) comes out of the physics.

   ⚠️ A raise or a lower CANNOT LOOP, and the export must not pretend it does:
   its last frame is a flag at the top and its first is a flag at the bottom.
   loopable() is what stops the loop-blend from being applied to them. */
const CEREMONY_SECONDS = 5.0;

function poleTravel() { return flagH() * 3.2; }

function loopable() { return state.ceremony === 'none' || state.ceremony === 'half_mast'; }

/**
 * Vertical offset of the attachment at time t, in metres (0 = at the truck).
 *
 * t is clamped at zero because the export's warm-up runs the clock NEGATIVE
 * (see simulate() in export.js): the cloth needs to fall into the flow before
 * recording starts, but the hoist must not have begun yet when it does.
 */
function hoistOffset(t, duration) {
    t = Math.max(0, t);
    const travel = poleTravel();
    switch (state.ceremony) {
        case 'half_mast':
            /* One flag height below the top — the actual convention, and the
               reason this is not just "a bit lower". */
            return -(flagH() * 1.15);
        case 'raise': {
            const d = Math.min(CEREMONY_SECONDS, Math.max(1.5, (duration || 10) * 0.6));
            const k = Math.min(1, Math.max(0, t / d));
            return -travel * (1 - ease01(k));
        }
        case 'lower': {
            const d = Math.min(CEREMONY_SECONDS, Math.max(1.5, (duration || 10) * 0.6));
            const k = Math.min(1, Math.max(0, t / d));
            return -travel * ease01(k);
        }
        default:
            return 0;
    }
}

/* Eased, not linear: a halyard is hauled by hand, so the flag starts and stops
   gently. A constant rate reads as a machine. */
function ease01(k) { return k * k * (3 - 2 * k); }

function anchorFor(duration) {
    if (state.ceremony === 'none') return null;
    if (!cloth) return null;
    return cloth.hoistAnchor(t => hoistOffset(t, duration));
}

function buildWind(period) {
    const b = BEAUFORT[Math.max(0, Math.min(12, state.beaufort))];
    return new Wind({
        speed: b.ms,
        direction: state.windDir,
        gustiness: state.gustiness,
        period: period || state.duration,
        seed: state.seed,
    });
}

/**
 * Apply the whole visual state to a scene. ONE function for the preview and
 * for both export scenes: they were three near-copies, and the camera-framing
 * calls landed in only one of them — so an exported Qatar would still have run
 * off the frame while the preview on screen was correct.
 */
function configureScene(s) {
    s.setFlagExtents(flagH() * state.aspect, flagH());
    s.setZoom(state.zoom);
    /* A hoist shot has to hold the whole pole, or the flag climbs out of the
       frame. Half-mast only needs the drop, not the full travel. */
    s.setTravel(state.ceremony === 'raise' || state.ceremony === 'lower' ? poleTravel()
        : state.ceremony === 'half_mast' ? flagH() * 1.15 : 0);
    s.setExtension(extensionAt(BEAUFORT[state.beaufort].ms, fabricCfg().gsm));
    s.setBackground(state.background);
    s.setLighting(state.lighting);
    s.setCamera(state.camera);
    s.setGroundShadow(state.groundShadow && state.background !== 'transparent');
    s.setPole(state.pole === 'none' ? null : {
        type: state.pole,
        finial: state.finial,
        color: state.pole === 'wood' ? 0x8a6a44 : 0xd8dade,
    });
    return s;
}

/**
 * The transparency note.
 *
 * Shown whenever a transparent background is selected, because it states a
 * fact about THIS TOOL's export — the video path never carries alpha — not
 * about the browser. It used to be gated on a WebCodecs capability probe, so
 * on a Chrome that supports VP9 alpha the warning vanished and the file still
 * came back opaque. A capability answer cannot stand in for a behaviour claim.
 */
function syncAlphaNote() {
    const note = $('cfAlphaNote');
    if (note) note.hidden = state.background !== 'transparent';
}

function applyScene() {
    if (scene) configureScene(scene);
}


async function applyFlag() {
    /* A user image is treated as just another flag: it keeps ITS OWN aspect
       ratio, exactly like a country's does. Stretching an upload to some
       default frame would contradict the one thing this tool is about. */
    const custom = state.customImage;
    const f = custom || manifest.byCode[state.code];
    if (!f) return;
    if (!scene) return;                 // catalogue-only mode: nothing to dress
    state.aspect = f.ar;
    state.kind = custom ? 'custom' : f.k;
    state.shape = (custom ? null : f.shape) || 'rect';

    const { width } = outputSize(state.resolution, state.outAspect);
    const texW = Math.min(4096, Math.max(1024, Math.round(width * 1.15)));
    /* Texture width follows the export target, not the preview: re-rasterising
       a 190 KB coat-of-arms SVG at export time would stall the encoder loop. */
    flagTex = custom
        ? custom.tex
        : await loadFlag(state.code, f.ar, texW, { needsMask: state.shape === 'pennon' });

    cloth = buildCloth('preview');
    cloth.captureAnchors();
    wind = buildWind();
    const fc = fabricCfg();
    scene.setFlag(cloth, {
        flagCanvas: flagTex.canvas,
        aspect: f.ar,
        masked: !!flagTex.mask,
        sheen: fc.sheen,
        roughness: fc.roughness,
        translucency: state.fabric === 'heavy' ? 0.34 : state.fabric === 'satin' ? 0.7 : 0.55,
        weave: state.fabric === 'satin' ? 0.18 : 0.38,
        wear: state.wear,
        ao: 0.6,
    });
    applyScene();
    renderCard(custom ? { c: 'custom', k: 'custom', ar: f.ar, name: f.name, src: custom.url } : f,
               flagTex?.colors);
    const badge = $('cfStageBadge');
    if (badge) badge.textContent = `${labelFor(f)} · ${ratioLabel(custom ? { ar: f.ar } : f)}`;
}

/**
 * Does this platform compose a regional-indicator pair into a flag?
 *
 * Measured, not guessed from the user agent: a composed pair is about one glyph
 * wide, an uncomposed one is two letters wide. Windows composes nothing, which
 * is why «UA» showed up where a flag was expected.
 */
let flagEmojiOk = null;
function canRenderFlagEmoji() {
    if (flagEmojiOk !== null) return flagEmojiOk;
    try {
        const cx = document.createElement('canvas').getContext('2d');
        cx.font = '24px sans-serif';
        const pair = cx.measureText('\uD83C\uDDFA\uD83C\uDDE6').width;
        const one = cx.measureText('\uD83C\uDDFA').width;
        flagEmojiOk = one > 0 && pair < one * 1.6;
    } catch (_) {
        flagEmojiOk = false;
    }
    return flagEmojiOk;
}

/** Regional-indicator pair — «флаг эмодзи», 14 795 searches a month, free
    from the ISO code we already have. Only real two-letter country codes get
    one: `gb-eng` and `hist-ussr` have no emoji and inventing one would show
    two blank boxes. */
function emojiFor(f) {
    if (!f || f.k !== 'country' || !/^[a-z]{2}$/.test(f.c)) return '';
    const A = 0x1F1E6;
    return String.fromCodePoint(A + f.c.charCodeAt(0) - 97, A + f.c.charCodeAt(1) - 97);
}

/** How far apart two palettes are, weighted by how much of the flag each
    colour covers — so a difference in a field counts and one in a crest does
    not. */
function paletteDist(a, b) {
    if (!a.p || !b.p) return 1e9;
    const rgb = h => [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    let sum = 0;
    for (const c of a.p.slice(0, 4)) {
        const [r1, g1, b1] = rgb(c.h);
        let best = 1e9;
        for (const d of b.p.slice(0, 4)) {
            const [r2, g2, b2] = rgb(d.h);
            best = Math.min(best, Math.abs(r1 - r2) + Math.abs(g1 - g2) + Math.abs(b1 - b2));
        }
        sum += best * (c.s / 100);
    }
    return sum;
}

/**
 * The flags people mix up: same colours, same band direction — Chad and
 * Romania, Indonesia and Monaco, Ireland and Côte d'Ivoire. Computed from the
 * palette data the filter already uses, so it cannot drift from what the page
 * says the colours are.
 */
function similarTo(f) {
    if (!f?.cl?.length || !manifest) return [];
    const key = f.cl.slice().sort().join('+');
    const same = manifest.flags.filter(g => g.c !== f.c && g.cl && g.cl.slice().sort().join('+') === key);
    const sameLay = f.lay && f.lay !== 'other' ? same.filter(g => g.lay === f.lay) : [];
    const pool = sameLay.length >= 2 ? sameLay : same;
    return pool
        .map(g => ({ g, d: paletteDist(f, g) }))
        .sort((x, y) => x.d - y.d)
        .slice(0, 8)
        .map(x => x.g);
}

/**
 * The flag card: what the flag IS and how to take it.
 *
 * Everything here is DERIVED — the ratio from the SVG's own viewBox at build
 * time, the colours from the rasterised pixels by the very function the
 * renderer uses — so it cannot drift from what the tool draws, and there is no
 * hand-kept table to go stale when a country restyles its flag.
 *
 * It renders SYNCHRONOUSLY on selection. The 3D texture behind it takes a
 * moment to rasterise, and making the answer to «which country is this» wait
 * on the animation engine would be backwards.
 */
function renderCard(f, runtimeColors) {
    if (!f) return;
    const name = labelFor(f);
    const title = $('cfCardName');
    if (title) title.textContent = name;

    /* The header of the left column carries the identity: name, proportion,
       emoji. They used to live inside the facts list, which is now below the
       picture — a flag has to say what it is above its own portrait. */
    const badge = $('cfRatioBadge');
    if (badge) badge.textContent = ratioLabel(f);
    const emo = $('cfEmoji');
    if (emo) {
        const e = emojiFor(f);
        emo.hidden = !e;
        emo.dataset.hex = e;
        if (e) {
            /* ⚠️ Windows НЕ РИСУЕТ флаги-эмодзи: пара региональных индикаторов
               остаётся буквами «UA», и выглядит это как наш баг (Фарид так и
               спросил — «почему эмодзи показывается текстом?»). Там, где
               система их не составляет, показываем сам флаг картинкой и код
               буквами; копируется при этом настоящий символ — он отрисуется
               там, куда его вставят. */
            /* Где система рисует флаги-эмодзи — показываем символ. Где нет
               (Windows) — сам флаг картинкой: это и есть то, как эмодзи
               выглядит. Буквы «ZW» из этой ветки убраны: они читались как
               ошибка. Копируется в обоих случаях настоящий символ. */
            emo.innerHTML = canRenderFlagEmoji()
                ? escapeHtml(e)
                : `<img src="/data/flags/svg/${f.c}.svg" alt="">`;
            emo.title = canRenderFlagEmoji()
                ? t('emoji.copy', 'Скопировать эмодзи флага')
                : t('emoji.copy', 'Скопировать эмодзи флага') + ' — ' + t('emoji.nofont', '');
        }
    }

    const img = $('cfFlatImg');
    if (img) {
        img.src = f.k === 'custom' ? (f.src || '') : `/data/flags/svg/${f.c}.svg`;
        img.alt = name;
        img.style.aspectRatio = String(f.ar);
    }

    /* Manifest palette first: it is the same measurement the filter runs on,
       so the swatches and the chips can never disagree. A user's own picture
       has no manifest row and falls back to its live analysis. */
    const cols = (f.p && f.p.length)
        ? f.p.filter(c => c.s >= 1).map(c => ({ hex: '#' + c.h, share: c.s / 100 }))
        : (runtimeColors || []);

    const el = $('cfFacts');
    if (!el) return;
    const swatches = cols.map(c =>
        `<button type="button" class="cf-sw" data-hex="${c.hex}" title="${escapeHtml(t('copy', 'Скопировать'))} ${c.hex}">
            <span class="cf-sw-dot" style="background:${c.hex}"></span>
            <span class="cf-sw-hex">${c.hex.toUpperCase()}</span>
            <span class="cf-sw-share">${Math.round(c.share * 100)}%</span>
        </button>`).join('');
    const detail = ratioDetail(f);
    const emoji = emojiFor(f);

    el.innerHTML =
        `<div class="cf-fact"><span class="cf-fact-k">${escapeHtml(t('facts.ratio', 'Пропорция'))}</span>` +
        `<span class="cf-fact-v">${escapeHtml(ratioLabel(f))}</span>` +
        (detail ? `<span class="cf-fact-note">${escapeHtml(detail)}</span>` : '') + `</div>` +
        /* ⚠️ Эмодзи ЖИВЁТ В ШАПКЕ, и только там. Здесь стоял его дубль, который
           рисовал СЫРОЙ символ: на Windows это буквы «ZW» рядом со словом
           «Эмодзи» — выглядит как поломка, а не как флаг. Одна поверхность на
           одну вещь. */
        `<div class="cf-fact" style="flex:1 1 100%"><span class="cf-fact-k">${escapeHtml(t('facts.colors', 'Цвета'))}</span>` +
        `<span class="cf-fact-v cf-sws">${swatches}</span></div>`;

    const sim = similarTo(f);
    const wrap = $('cfSimilarWrap');
    if (wrap) wrap.hidden = sim.length === 0;
    renderWall($('cfSimilar'), sim);
}

// ───────────────────────────── preview loop ─────────────────────────────

function tick(now) {
    rafId = requestAnimationFrame(tick);
    /* `wind` в проверке наравне с остальными: он разыменовывается ниже
     * безусловно, а rebuild() пересобирает ТОЛЬКО cloth — пара «cloth есть,
     * wind нет» достижима, и она давала `wind.sampler()` на null (Sentry FZ). */
    if (!running || busy || !cloth || !scene || !wind) return;

    const dt = lastT ? Math.min(0.05, (now - lastT) / 1000) : 1 / 60;
    lastT = now;

    const t0 = performance.now();
    /* Fixed timestep regardless of display rate: the same wall-clock second of
       wind must be the same second of flag on 60 Hz and on 120 Hz. */
    const sub = frameCost > 26 ? 4 : 6;
    const step = dt / sub;
    const sampler = wind.sampler();
    const anc = anchorFor(state.duration);
    for (let i = 0; i < sub; i++) cloth.step(step, sampler, { iterations: 3, anchor: anc });
    scene.syncCloth();
    scene.render();

    /* Rolling cost, used to shed substeps rather than to drop frames — a flag
       that keeps moving smoothly at slightly lower fidelity beats one that
       hitches. Physics stays deterministic in the EXPORT either way. */
    frameCost = frameCost * 0.9 + (performance.now() - t0) * 0.1;

    /* Debug surface for the Playwright probes. Physics cost is the one number
       that decides the grid, and it can only be honestly measured on real
       hardware in a real browser — a Node timing says nothing about a phone. */
    if (window.__CF_DEBUG) {
        window.__CF_DEBUG.frameCost = frameCost;
        window.__CF_DEBUG.grid = cloth ? [cloth.nx, cloth.ny, cloth.n, cloth.triCount] : null;
        /* The fabric uniforms, so a probe can switch ONE term off and measure
           what it was contributing. The backlit transmission was silently dead
           for a while — view-space normal against a world-space sun — and
           nothing but this kind of A/B would have shown it. */
        window.__CF_DEBUG.uniforms = scene?.material?.userData?.uniforms || null;
        window.__CF_DEBUG.material = scene?.material || null;
        /* Draw one frame on demand. A/B-ing a shader term needs the SAME
           geometry in both grabs; with the loop running the flag moves between
           them and the difference is dominated by motion, and with the loop
           paused nothing repaints at all — the first attempt at this
           measurement returned all zeros for exactly that reason. */
        window.__CF_DEBUG.renderOnce = () => { scene?.syncCloth(); scene?.render(); };
        window.__CF_DEBUG.frame = scene ? {
            code: state.code, aspect: state.aspect,
            flagW: scene.flagW, flagH: scene.flagH,
            extension: scene._extension, travel: scene.travel, poleTop: scene._poleTop,
            camDist: Math.hypot(scene.camera.position.x, scene.camera.position.y, scene.camera.position.z),
            camPos: [scene.camera.position.x, scene.camera.position.y, scene.camera.position.z].map(v => +v.toFixed(2)),
            fov: scene.camera.fov,
        } : null;
    }
}

function start() { if (!rafId) { lastT = 0; rafId = requestAnimationFrame(tick); } running = true; }
function stop() { running = false; if (rafId) { cancelAnimationFrame(rafId); rafId = 0; } }

function resizePreview() {
    const wrap = $('cfStage');
    if (!wrap || !scene) return;
    const a = ASPECTS[state.outAspect] ?? 16 / 9;
    const w = wrap.clientWidth;
    const h = Math.round(w / a);
    wrap.style.height = h + 'px';
    scene.setSize(w, h, Math.min(devicePixelRatio || 1, 2));
}

// ───────────────────────────── export ─────────────────────────────

function yieldToUi() { return new Promise(r => setTimeout(r, 0)); }

function setProgress(p, phase) {
    const bar = $('cfProgBar'), lbl = $('cfProgLabel');
    if (bar) bar.style.width = Math.round(p * 100) + '%';
    if (lbl) lbl.textContent = t('phase.' + phase, phase) + ' · ' + Math.round(p * 100) + '%';
}

function showProgress(on) {
    const box = $('cfProgress');
    if (box) box.hidden = !on;
    /* An export needs the scene; pressing the button is as clear a request
       for it as flipping the switch. */
    document.querySelectorAll('[data-cf-export]').forEach(b => { b.disabled = on; });
}

/**
 * This browser cannot encode video: say so where the button is, not at the end
 * of a render. The still and the PNG sequence keep working, so the tool
 * degrades to what it CAN do instead of failing.
 */
function markVideoUnsupported(reason) {
    document.querySelectorAll('[data-cf-export="video"], [data-cf-export="gif"]').forEach(b => {
        b.disabled = true;
    });
    const note = $('cfNoEncoder');
    if (note) note.hidden = false;
    window.__aeDiagnostic?.({ issue: 'no_video_encoder', reason: String(reason).slice(0, 40) });
}

/** Ask once on boot so the button never promises what the browser cannot do. */
async function checkEncoder() {
    if (typeof VideoEncoder === 'undefined') { markVideoUnsupported('no-videoencoder'); return; }
    const { width, height } = outputSize(state.resolution, state.outAspect);
    try {
        const s2 = await window.CanvasVideoExport?.isSupported({
            width, height, fps: state.fps, format: state.format,
        });
        if (s2 && !s2.ok) markVideoUnsupported(s2.reason || 'unsupported');
    } catch (_) { /* the export path re-checks; a probe failure is not a verdict */ }
}

let abortCtl = null;

async function runExport(kind) {
    if (busy) return;

    /* One token per EXPORT, taken before any work and released on any failure
       or cancel — a flag the user never received is not a run. Stills are not
       metered at all: the flat PNG/SVG of a flag is the informational half of
       this page, and putting a daily wall on "download the flag of Germany"
       would break the thing most visitors came for. */
    const token = window.__toolLimit
        ? window.__toolLimit.begin(SLUG, [`${state.code}:${kind}:${state.resolution}:${state.duration}`])
        : { commit() {}, abort() {} };
    if (!token) return;

    busy = true;
    stop();
    showProgress(true);
    abortCtl = new AbortController();
    const t0 = performance.now();

    try {
        const { width, height } = outputSize(state.resolution, state.outAspect);
        stage('exporting', { kind });
        window.__aeFunnel?.('export_start', Object.assign({ kind }, shot()));

        /* ⚠️ ASK THE ENCODER FIRST. This check used to live inside
           encodeVideo(), i.e. AFTER the solver had run every frame — so a
           browser without WebCodecs (WebKit reports no VideoEncoder at all)
           made the user sit through a full render and then handed them an
           error. Found on an iPhone 13 viewport, invisible on the desktop
           where the API always exists. */
        if (kind === 'video') {
            const support = await window.CanvasVideoExport?.isSupported({
                width, height, fps: state.fps, format: state.format,
            }).catch(() => ({ ok: false, reason: 'probe-failed' }));
            if (!support || !support.ok) {
                markVideoUnsupported(support?.reason || 'no-webcodecs');
                token.abort();
                busy = false;
                showProgress(false);
                start();
                return;
            }
        }

        const exportCloth = buildCloth('export');
        const exportWind = buildWind(state.duration);
        const sim = await simulate(exportCloth, exportWind, {
            fps: state.fps,
            duration: state.duration,
            substeps: 8,
            iterations: 4,
            /* A raise or a lower is not a loop, and blending its tail into its
               head would slide the flag back down the pole in the last half
               second. */
            loop: loopable(),
            anchor: exportCloth.hoistAnchor
                ? (state.ceremony === 'none' ? null : exportCloth.hoistAnchor(t => hoistOffset(t, state.duration)))
                : null,
            warmup: state.ceremony === 'raise' || state.ceremony === 'lower' ? 0.6 : 3.5,
            signal: abortCtl.signal,
            onProgress: setProgress,
            yield: yieldToUi,
        });

        /* Render at the true output size, off the DOM canvas: the preview may
           be 640 px wide on a phone and the file must not inherit that. */
        const off = document.createElement('canvas');
        off.width = width; off.height = height;
        const exportScene = new FlagScene(THREE, off, { pixelRatio: 1, antialias: true });
        exportScene.setFlag(exportCloth, sceneFlagOpts());
        configureScene(exportScene);
        exportScene.setSize(width, height, 1);

        const paint = (i) => {
            exportCloth.pos.set(sim.frames[i]);
            exportCloth.computeNormals();
            exportScene.syncCloth();
            exportScene.render();
        };

        let blob, filename;
        const base = `flag-${state.code}-${state.resolution}`;
        if (kind === 'video') {
            blob = await encodeVideo(sim, off, {
                width, height, format: state.format, crf: 18,
                signal: abortCtl.signal, onProgress: setProgress, paint,
            });
            filename = `${base}.${state.format}`;
        } else if (kind === 'sequence') {
            blob = await encodePngSequence(sim, off, {
                name: `flag-${state.code}`, signal: abortCtl.signal,
                onProgress: setProgress, yield: yieldToUi, paint,
            });
            filename = `${base}-png-sequence.zip`;
        } else if (kind === 'gif') {
            blob = await encodeGif(sim, off, {
                width, height, workerScript: window.__CF_GIF_WORKER,
                signal: abortCtl.signal, onProgress: setProgress, yield: yieldToUi, paint,
            });
            filename = `${base}.gif`;
        }

        exportScene.dispose();
        if (!blob) throw new Error('empty-output');

        window.__downloadFile(blob, filename);
        token.commit();
        window.__aeToolComplete?.(Object.assign({
            kind, bytes: blob.size,
            seconds: Math.round((performance.now() - t0) / 1000),
            /* How long a render really takes on real hardware, alongside what
               was rendered — the pair is what would justify (or refute) any
               future limit on duration or resolution. */
            grid: exportCloth ? exportCloth.nx + 'x' + exportCloth.ny : null,
        }, shot()));
        window.__toast?.(t('done', 'Готово'), 'success');
    } catch (e) {
        token.abort();
        if (e?.name === 'AbortError') {
            window.__toast?.(t('cancelled', 'Отменено'), 'info');
        } else {
            window.__aeDiagnostic?.(Object.assign({
                issue: e?.reason ? 'encoder_' + e.reason : 'export_failed',
                msg: String(e?.message || e).slice(0, 160),
            }, shot()));
            window.__handleError?.(e, 'country-flags/export');
        }
    } finally {
        busy = false;
        abortCtl = null;
        showProgress(false);
        start();
    }
}

function sceneFlagOpts() {
    const fc = fabricCfg();
    return {
        flagCanvas: flagTex.canvas,
        aspect: state.aspect,
        masked: !!flagTex.mask,
        sheen: fc.sheen,
        roughness: fc.roughness,
        translucency: state.fabric === 'heavy' ? 0.34 : state.fabric === 'satin' ? 0.7 : 0.55,
        weave: state.fabric === 'satin' ? 0.18 : 0.38,
        wear: state.wear,
        ao: 0.6,
    };
}

/** Current preview frame as a PNG — free, and the fastest way to a poster. */
async function exportFrame() {
    if (!scene || busy) return;
    const { width, height } = outputSize(state.resolution, state.outAspect);
    const off = document.createElement('canvas');
    off.width = width; off.height = height;
    const s = new FlagScene(THREE, off, { pixelRatio: 1 });
    /* setFlag first: configureScene()'s setLighting pushes the sun into the
       fabric material's uniforms, and there is no material until the flag
       exists — run it the other way round and the backlit transmission is
       silently left at its default. */
    s.setFlag(cloth, sceneFlagOpts());
    configureScene(s);
    s.setSize(width, height, 1);
    s.syncCloth();
    s.render();
    const blob = await new Promise(r => off.toBlob(r, 'image/png'));
    s.dispose();
    window.__downloadFile(blob, `flag-${state.code}-${width}x${height}.png`);
    window.__aeFunnel?.('still_png', { code: state.code, res: state.resolution });
}

/** Flat flag, no 3D — the "I just want the flag" path. Never metered. */
async function exportFlat(shape, width) {
    if (state.customImage) {
        /* An uploaded picture has no catalogue entry to look up, so the flat
           export is cut from the picture itself — same shapes, same centred
           crop, so a logo comes back as a circle avatar rather than squashed. */
        const src = state.customImage.tex.canvas;
        const out = document.createElement('canvas');
        const square = shape === 'square' || shape === 'circle';
        out.width = square ? width : width;
        out.height = square ? width : Math.round(width / state.customImage.ar);
        const cx = out.getContext('2d');
        if (shape === 'circle') {
            cx.beginPath();
            cx.arc(out.width / 2, out.height / 2, Math.min(out.width, out.height) / 2, 0, Math.PI * 2);
            cx.clip();
        } else if (shape === 'rounded') {
            cx.beginPath();
            cx.roundRect(0, 0, out.width, out.height, Math.min(out.width, out.height) * 0.12);
            cx.clip();
        }
        const sc = square ? Math.max(out.width / src.width, out.height / src.height)
            : out.width / src.width;
        const dw = src.width * sc, dh = src.height * sc;
        cx.drawImage(src, (out.width - dw) / 2, (out.height - dh) / 2, dw, dh);
        const b = await new Promise(r => out.toBlob(r, 'image/png'));
        window.__downloadFile(b, `flag-custom-${shape}-${width}.png`);
        return;
    }
    const f = manifest.byCode[state.code];
    const canvas = await renderStill(state.code, f.ar, width, { shape });
    const blob = await new Promise(r => canvas.toBlob(r, 'image/png'));
    window.__downloadFile(blob, `flag-${state.code}-${shape}-${width}.png`);
    window.__aeFunnel?.('flat_png', { code: state.code, shape, width });
}

async function exportSvg() {
    /* There is no vector to hand back for an uploaded raster, and inventing one
       (tracing, or wrapping the PNG in an <svg>) would deliver a file that
       claims to be something it is not. */
    if (state.customImage) {
        window.__toast?.(t('custom.no_svg', 'Для своей картинки вектора нет'), 'info');
        return;
    }
    const r = await fetch(`/data/flags/svg/${state.code}.svg`);
    const text = await r.text();
    window.__downloadFile(new Blob([text], { type: 'image/svg+xml' }), `flag-${state.code}.svg`);
    window.__aeFunnel?.('flat_svg', { code: state.code });
}

// ───────────────────────────── UI wiring ─────────────────────────────

function bindWindUi() {
    const slider = $('cfWind');
    const out = $('cfWindOut');
    const paint = () => {
        const b = BEAUFORT[state.beaufort];
        const u = windUnits(b.ms);
        if (out) {
            /* Each measurement is its own LTR-isolated run. In one span the
               Arabic unit words turn the digits into Arabic-numeric and the
               "·" separators stay neutral, so the three groups render in
               reverse order — the screen disagrees with the value while every
               text assertion still passes. */
            const num = (v, unit) =>
                `<span class="cf-num">${escapeHtml(String(v))} ${escapeHtml(unit)}</span>`;
            out.innerHTML =
                `<strong>${escapeHtml(t('beaufort.' + b.name, b.name))}</strong>` +
                `<span class="cf-wind-num">` +
                    num(b.f, t('bft', 'балл')) + ' · ' +
                    num(u.ms.toFixed(1), t('unit.ms', 'м/с')) + ' · ' +
                    num(Math.round(u.kmh), t('unit.kmh', 'км/ч')) +
                `</span>` +
                `<span class="cf-wind-desc">${escapeHtml(t('beaufort.desc.' + b.name, ''))}</span>`;
        }
    };
    slider?.addEventListener('input', () => {
        state.beaufort = +slider.value;
        wind?.set({ speed: BEAUFORT[state.beaufort].ms });
        scene?.setExtension(extensionAt(BEAUFORT[state.beaufort].ms, fabricCfg().gsm));
        paint();
    });
    paint();
}

function fillSelect(id, entries, current) {
    const el = $(id);
    if (!el) return;
    el.innerHTML = entries.map(([v, label]) =>
        `<option value="${escapeHtml(v)}"${v === current ? ' selected' : ''}>${escapeHtml(label)}</option>`
    ).join('');
}

/**
 * Left column: the flag as a picture, or the flag in the wind.
 *
 * The picture is the default because it is the answer to the question most
 * people came with, and it costs nothing to draw. Asking for motion is what
 * boots the engine.
 */
async function setView(view) {
    const wind = view === 'wind';
    document.querySelectorAll('[data-cf-view]').forEach(b =>
        b.classList.toggle('is-on', b.dataset.cfView === view));
    const flat = $('cfFlatWrap');
    const st = $('cfStage');
    if (flat) flat.hidden = wind;
    if (st && !st.dataset.cfDead) st.hidden = !wind;
    if (!wind) return;
    const ok = await ensureStudio();
    if (ok) { resizePreview(); start(); }
}

/** Which artwork we are working with: a country flag, or the visitor's own. */
function setSource(src) {
    document.querySelectorAll('[data-cf-src]').forEach(b => {
        const on = b.dataset.cfSrc === src;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-cf-srcpane]').forEach(el =>
        el.classList.toggle('is-on', el.dataset.cfSrcpane === src));
}

/**
 * What we are downloading — and therefore which settings are worth showing.
 *
 * Before this the page offered wind, fabric and scene next to a button that
 * produced a FLAT PNG, so a visitor could spend a minute tuning a breeze and
 * get a file where none of it appears. The choice of file now drives the
 * settings: «картинка флага» hides the scene entirely and says why, everything
 * else brings it back and switches the preview to the cloth.
 */
function setWhat(what, opts) {
    state.what = what;
    document.querySelectorAll('[data-cf-for]').forEach(el => {
        el.hidden = !el.dataset.cfFor.split(' ').includes(what);
    });
    document.querySelectorAll('[data-cf-go]').forEach(el => {
        el.hidden = el.dataset.cfGo !== what;
    });

    const needsScene = what !== 'flat';
    document.querySelectorAll('[data-cf-needs="scene"]').forEach(el =>
        el.classList.toggle('is-off', !needsScene));
    const off = $('cfSceneOff');
    if (off) off.hidden = needsScene;

    /* ⚠️ НА ЗАГРУЗКЕ ВИД НЕ ТРОГАЕМ. Тип файла по умолчанию — «Видео», и
       переключение предпросмотра здесь означало бы, что движок поднимается
       у КАЖДОГО открывшего страницу — ровно то, от чего уходили. Сторож это
       и поймал: «на открытии three.js не загружен — 1 запрос». */
    if (!opts || !opts.boot) setView(needsScene ? 'wind' : 'flat');
    syncAlphaNote();
    if (!opts || !opts.boot) window.__aeFunnel?.('export_kind', { what });
}

/**
 * Потолок длительности ролика по тарифу.
 *
 * ⚠️ Не молчаливое урезание, а ОБЪЯВЛЕННАЯ граница: ползунок физически не
 * уезжает дальше, и рядом написано, сколько даёт Премиум. Тихое снижение
 * качества в этом проекте запрещено отдельным решением (2026-07-07) и
 * сторожится тестом — кодек здесь не трогается вовсе.
 *
 * Снимок тарифа приходит асинхронно вместе с /api/session-bootstrap. Судить
 * до него — значит пропустить всех; поэтому применяется и на старте, и по
 * событию, когда снимок доехал.
 */
function applyDurationCap() {
    const el = $('cfDuration');
    if (!el || !window.__toolLimit || !window.__toolLimit.durationCap) return;
    const cap = window.__toolLimit.durationCap(SLUG) | 0;
    /* ⚠️ premiumDurationCap() в примитиве ВНУТРЕННЯЯ — наружу выведен только
       durationCap. Число Премиума берём из атрибута, который рисует
       <x-tool-limit> прямо из конфига: это его же источник. Через несуществующую
       функцию подпись молча не показывалась — потолок применялся, а сказать о
       нём было нечем. */
    const root = document.querySelector('[data-tl-dur-premium]');
    const paid = root ? (parseInt(root.getAttribute('data-tl-dur-premium'), 10) || 0) : 0;
    const hard = +el.getAttribute('data-cf-max') || +el.max || 30;
    if (!el.getAttribute('data-cf-max')) el.setAttribute('data-cf-max', String(hard));

    const max = cap > 0 ? Math.min(cap, hard) : hard;
    el.max = String(max);
    if (+el.value > max) {
        el.value = String(max);
        state.duration = max;
        el.dispatchEvent(new Event('input', { bubbles: true }));
    }
    const note = $('cfDurCap');
    if (note) {
        /* Говорим только когда потолок ДЕЙСТВУЕТ. «До 30 с» у того, у кого и
           так 30, — шум, а не информация. */
        const show = cap > 0 && cap < hard && paid > cap;
        note.hidden = !show;
        if (show) {
            note.textContent = t('dur.cap', 'До :n с. С Премиумом — до :p с.')
                .replace(':n', String(cap)).replace(':p', String(paid));
        }
    }
}

function bind() {
    $('cfSearch')?.addEventListener('input', debounce(buildPicker, 120));
    $('cfRegion')?.addEventListener('change', buildPicker);

    /* Every wall selects: the results, the wider set, and the similar-flags
       row — picking a look-alike is how you tell Chad from Romania. */
    ['cfGrid', 'cfGridAlso', 'cfSimilar'].forEach(id => $(id)?.addEventListener('click', async (e) => {
        const b = e.target.closest('.cf-flag');
        if (!b) return;
        await pickFlag(b.dataset.code, id);
    }));

    $('cfColors')?.addEventListener('click', (e) => {
        const b = e.target.closest('[data-cf-color]');
        if (!b) return;
        const c = b.dataset.cfColor;
        if (filters.colors.has(c)) filters.colors.delete(c); else filters.colors.add(c);
        buildPicker();
        stage('tuned', { first: 'color' });
        window.__aeFunnel?.('color_filter', { colors: [...filters.colors].join('+'), n: $('cfCount')?.textContent });
    });

    document.querySelectorAll('[data-cf-layout]').forEach(b => b.addEventListener('click', () => {
        filters.layout = filters.layout === b.dataset.cfLayout ? null : b.dataset.cfLayout;
        buildPicker();
        window.__aeFunnel?.('layout_filter', { layout: filters.layout || 'off' });
    }));

    $('cfReset')?.addEventListener('click', () => {
        filters.colors.clear();
        filters.layout = null;
        buildPicker();
    });

    document.querySelectorAll('[data-cf-view]').forEach(b =>
        b.addEventListener('click', () => setView(b.dataset.cfView)));
    document.querySelectorAll('[data-cf-src]').forEach(b =>
        b.addEventListener('click', () => setSource(b.dataset.cfSrc)));
    $('cfWhat')?.addEventListener('change', (e) => setWhat(e.target.value));
    $('cfEmpty')?.addEventListener('click', (e) => {
        if (!e.target.closest('[data-cf-clear]')) return;
        filters.colors.clear();
        filters.layout = null;
        buildPicker();
    });

    /* Touching any knob of the studio means the visitor wants motion, so the
       engine comes up without them having to find the switch first. */
    ['cfWind', 'cfDir', 'cfGust', 'cfFabric', 'cfWear', 'cfMount', 'cfSize', 'cfZoom',
     'cfCeremony', 'cfBackground', 'cfLighting', 'cfCamera', 'cfPole', 'cfFinial',
     'cfGroundShadow', 'cfReseed',
    ].forEach(id => $(id)?.addEventListener('input', () => setView('wind'), { once: true }));
    ['cfFabric', 'cfMount', 'cfSize', 'cfCeremony', 'cfBackground', 'cfLighting',
     'cfCamera', 'cfPole', 'cfFinial', 'cfGroundShadow',
    ].forEach(id => $(id)?.addEventListener('change', () => setView('wind'), { once: true }));
    $('cfReseed')?.addEventListener('click', () => setView('wind'), { once: true });
    /* Ответ на нажатие: кнопка меняет случайный рисунок порывов при той же силе
       ветра, и без подтверждения это читалось как «не работает». */
    $('cfReseed')?.addEventListener('click', () => {
        const hint = $('cfReseedHint');
        if (!hint) return;
        hint.textContent = t('reseed.done', 'Новый рисунок порывов');
        hint.classList.add('is-flash');
        clearTimeout(hint.__t);
        hint.__t = setTimeout(() => {
            hint.textContent = hint.dataset.base || '';
            hint.classList.remove('is-flash');
        }, 1800);
    });
    /* При нуле баллов менять нечего: полотно висит на собственном весе.
       Выключенная кнопка честнее нажатия без эффекта. */
    const syncReseed = () => {
        const b = $('cfReseed');
        if (b) b.disabled = (+($('cfWind')?.value ?? 4) === 0);
    };
    $('cfWind')?.addEventListener('input', syncReseed);
    syncReseed();

    $('cfEmoji')?.addEventListener('click', (e) => {
        const v = e.currentTarget.dataset.hex;
        if (!v) return;
        navigator.clipboard?.writeText(v);
        window.__toast?.(v + ' — ' + t('copied', 'скопировано'), 'success');
    });

    $('cfFacts')?.addEventListener('click', (e) => {
        const sw = e.target.closest('.cf-sw');
        if (!sw) return;
        navigator.clipboard?.writeText(sw.dataset.hex);
        window.__toast?.(sw.dataset.hex + ' — ' + t('copied', 'скопировано'), 'success');
    });

    bindWindUi();

    /* One listener marks engagement for every control, so a new knob cannot
         quietly fall out of the funnel by nobody remembering to instrument it. */
    ['cfWind', 'cfDir', 'cfGust', 'cfFabric', 'cfWear', 'cfMount', 'cfSize', 'cfZoom',
     'cfCeremony', 'cfBackground', 'cfLighting', 'cfCamera', 'cfPole', 'cfFinial',
     'cfResolution', 'cfOutAspect', 'cfDuration', 'cfFps', 'cfFormat', 'cfQuality',
    ].forEach(id => {
        const el = $(id);
        el?.addEventListener('change', () => stage('tuned', { first: id }), { once: true });
        el?.addEventListener('input', () => stage('tuned', { first: id }), { once: true });
    });

    on('cfDir', 'input', v => {
        state.windDir = +v;
        wind?.set({ direction: state.windDir });
        const o = $('cfDirOut'); if (o) o.textContent = v + '°';
    });
    on('cfGust', 'input', v => {
        state.gustiness = +v / 100;
        wind?.set({ gustiness: state.gustiness });
        const o = $('cfGustOut'); if (o) o.textContent = v + '%';
    });
    on('cfFabric', 'change', async v => { state.fabric = v; await rebuild(); });
    on('cfWear', 'input', v => {
        state.wear = +v / 100;
        const u = scene?.material?.userData?.uniforms;
        if (u) u.uWear.value = state.wear;
        const out = $('cfWearOut');
        if (out) out.textContent = Math.round(state.wear * 100) + '%';
    });
    on('cfSize', 'change', async v => { state.size = v; await rebuild(); });
    on('cfZoom', 'input', v => {
        state.zoom = +v / 100;
        scene?.setZoom(state.zoom);
        const out = $('cfZoomOut');
        if (out) out.textContent = Math.round(state.zoom * 100) + '%';
    });
    on('cfMount', 'change', async v => { state.pin = v; await rebuild(); });
    on('cfCeremony', 'change', async v => {
        state.ceremony = v;
        /* The badge is a CLAIM about the file, so it is withdrawn exactly when
           the claim stops being true. Written the other way round first, which
           left "the clip loops" on screen for a hoist — a movement with a
           beginning and an end. */
        const note = $('cfLoopNote');
        if (note) note.hidden = !loopable();
        await rebuild();
    });
    on('cfBackground', 'change', v => { state.background = v; applyScene(); syncAlphaNote(); });
    on('cfLighting', 'change', v => {
        state.lighting = v;
        const preset = LIGHTING[v];
        /* A lighting preset carries the sky it was lit for. Changing the time
           of day without the matching sky gives a golden-hour flag against a
           midday blue, which reads as a compositing mistake — so the sky
           follows unless the user has picked a deliberate flat/chroma one. */
        if (preset?.bg && !BACKGROUNDS[state.background]?.flat && state.background !== 'transparent') {
            state.background = preset.bg;
            const sel = $('cfBackground');
            if (sel) sel.value = preset.bg;
        }
        applyScene();
    });
    on('cfCamera', 'change', v => { state.camera = v; applyScene(); });
    on('cfPole', 'change', v => { state.pole = v; applyScene(); });
    on('cfFinial', 'change', v => { state.finial = v; applyScene(); });
    on('cfGroundShadow', 'change', (_, el) => { state.groundShadow = el.checked; applyScene(); });

    on('cfResolution', 'change', async v => { state.resolution = v; await applyFlag(); });
    on('cfOutAspect', 'change', v => { state.outAspect = v; resizePreview(); });
    on('cfDuration', 'input', v => {
        state.duration = +v;
        const l = $('cfDurationOut');
        if (l) { l.className = 'cf-wind-num cf-num'; l.textContent = state.duration + ' ' + t('unit.sec', 'с'); }
        wind?.set({ period: state.duration });
    });
    on('cfFps', 'change', v => { state.fps = +v; });
    on('cfFormat', 'change', v => { state.format = v; });
    on('cfQuality', 'change', async v => { state.quality = v; await rebuild(); });

    document.querySelectorAll('[data-cf-export]').forEach(b =>
        b.addEventListener('click', async () => {
            /* Pressing export is as clear a request for the engine as flipping
               the switch — and without this the button was dead until the
               visitor happened to look at the flag in motion first. */
            await setView('wind');
            if (!scene) return;
            runExport(b.dataset.cfExport);
        }));
    $('cfStillPng')?.addEventListener('click', exportFrame);
    $('cfSvg')?.addEventListener('click', exportSvg);
    $('cfFlatPng')?.addEventListener('click', () =>
        exportFlat($('cfFlatShape')?.value || 'rect', +($('cfFlatSize')?.value) || 2048));
    $('cfCancel')?.addEventListener('click', () => abortCtl?.abort());

    const drop = $('cfUpload');
    /* __bindUploaderFile, not addEventListener: on a slow connection the user
       can pick a file BEFORE this module has attached, and a raw listener
       drops that event on the floor — the tool then sits dead until the file
       is picked again. The helper replays a pending pick. */
    const onCustomImage = async (file) => {
        if (!file) return;
        try {
            const bmp = await createImageBitmap(file);
            const max = 4096;
            const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
            const c = document.createElement('canvas');
            c.width = Math.max(2, Math.round(bmp.width * scale));
            c.height = Math.max(2, Math.round(bmp.height * scale));
            const cx = c.getContext('2d');
            cx.drawImage(bmp, 0, 0, c.width, c.height);

            /* The palette and the alpha mask come from the SAME analysis the
               country flags get, so an uploaded logo with a transparent
               background is cut out of the cloth just like Nepal's pennon —
               it is one code path, not a special case. */
            const small = document.createElement('canvas');
            const aw = Math.min(c.width, 256);
            small.width = aw;
            small.height = Math.max(1, Math.round(aw * c.height / c.width));
            small.getContext('2d', { willReadFrequently: true })
                .drawImage(c, 0, 0, small.width, small.height);
            const data = small.getContext('2d').getImageData(0, 0, small.width, small.height).data;
            let hasAlpha = false;
            for (let i = 3; i < data.length; i += 4) { if (data[i] < 250) { hasAlpha = true; break; } }

            /* A small copy for the flag card. The working canvas can be 4096 px
               wide, and its data URL would be several megabytes of string
               handed to an <img> that displays it 300 px across. Without this
               the card had no picture at all for an uploaded image: the object
               carries a canvas, and the card asks for a src. */
            const prev = document.createElement('canvas');
            const pscale = Math.min(1, 512 / Math.max(c.width, c.height));
            prev.width = Math.max(1, Math.round(c.width * pscale));
            prev.height = Math.max(1, Math.round(c.height * pscale));
            prev.getContext('2d').drawImage(c, 0, 0, prev.width, prev.height);

            state.customImage = {
                c: 'custom',
                k: 'custom',
                name: file.name.replace(/\.[^.]+$/, ''),
                url: prev.toDataURL('image/png'),
                ar: c.width / c.height,
                tex: {
                    canvas: c, width: c.width, height: c.height,
                    aspect: c.width / c.height,
                    colors: palette(data, small.width, small.height, 8),
                    hasAlpha,
                    mask: hasAlpha ? alphaGrid(data, small.width, small.height, 96, 96) : null,
                },
            };
            setSource('custom');
            renderCard(state.customImage, state.customImage.tex.colors);
            if (scene) { await applyFlag(); await rebuild(); resizePreview(); }
            window.__aeFunnel?.('custom_image', { w: c.width, h: c.height });
        } catch (err) {
            window.__handleError?.(err, 'country-flags/upload');
        }
    };
    if (drop) {
        if (window.__bindUploaderFile) window.__bindUploaderFile(drop, onCustomImage);
        else drop.addEventListener('tool-uploader:file', e => onCustomImage(e.detail?.file));
    }
    drop?.addEventListener('tool-uploader:clear', async () => {
        state.customImage = null;
        await applyFlag();
        await rebuild();
        resizePreview();
    });

    $('cfReseed')?.addEventListener('click', async () => {
        state.seed = (Math.random() * 1e6) | 0;
        wind?.set({ seed: state.seed });
        window.__aeFunnel?.('reseed', {});
    });

    let ro;
    if (window.ResizeObserver) {
        ro = new ResizeObserver(debounce(resizePreview, 100));
        ro.observe($('cfStage'));
    } else {
        window.addEventListener('resize', debounce(resizePreview, 150));
    }

    /* Pause when off-screen. A WebGL loop with a cloth solver behind it is not
       something to leave running in a background tab. */
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop(); else if (!busy) start();
    });
    const io = new IntersectionObserver(es => {
        for (const e of es) {
            if (busy) continue;
            if (e.isIntersecting) start(); else stop();
        }
    }, { threshold: 0.01 });
    io.observe($('cfStage'));
}

async function rebuild() {
    /* Ручку студии могут повернуть РАНЬШЕ, чем поднялся движок: three.js
     * грузится по требованию (и правильно — 670 КБ), а «поднять движок» и
     * «сделать работу» висят РАЗНЫМИ слушателями на одном событии. Слушатели
     * вызываются по очереди синхронно: первый доходит до своего await и
     * отдаёт управление, второй тут же работает по ещё не поднятой сцене.
     * Ждать первого он не умеет — промиса он не видит.
     *
     * Отсюда падение `scene.setFlag` на null (Sentry FY). Хуже того, прежний
     * порядок успевал присвоить `cloth` ДО падения, и в кадре анимации
     * оставалась пара «cloth есть, wind нет» — это вторая issue, FZ. Одно
     * действие человека давало две ошибки.
     *
     * Поэтому движок дожидаемся ЗДЕСЬ, у единственной двери, и только потом
     * трогаем общее состояние. ensureStudio идемпотентен и сам зовёт applyFlag,
     * так что повернутая ручка ПРИМЕНЯЕТСЯ, а не молча теряется. */
    if (!scene) {
        const ok = await ensureStudio();
        /* Движка нет совсем (WebGL закрыт) — studioUnavailable уже сказал об
         * этом человеку; молча собирать ткань не для чего. */
        if (!ok || !scene) return;
    }
    const wasRunning = running;
    stop();
    cloth = buildCloth('preview');
    if (flagTex?.mask) {
        const s = maskSampler(flagTex.mask);
        cloth.setMask((u, v) => (s(u, v) > 0.5 ? 1 : 0));
    }
    cloth.captureAnchors();
    scene.setFlag(cloth, sceneFlagOpts());
    applyScene();
    if (wasRunning) start();
}

function on(id, evt, fn) {
    const el = $(id);
    el?.addEventListener(evt, () => fn(el.value, el));
}

function debounce(fn, ms) {
    let h;
    return (...a) => { clearTimeout(h); h = setTimeout(() => fn(...a), ms); };
}

function updateQuery(k, v) {
    const u = new URL(location.href);
    u.searchParams.set(k, v);
    return u.pathname + u.search;
}

// ───────────────────────────── boot ─────────────────────────────

async function boot() {
    const canvas = $('cfCanvas');
    if (!canvas) return;

    names = window.__CF_NAMES || {};

    /* ── Stage 1 · the catalogue ──────────────────────────────────────────
       Finding a flag, reading its colours and taking the PNG or the SVG need
       the manifest and nothing else — no WebGL, no three.js, no CDN. Until
       now all of that was booted behind the engine, so a blocked or slow
       jsdelivr took down the two jobs most visitors arrive with along with
       the one they usually do not. Different dependencies, different stages. */
    try {
        await loadManifest();
    } catch (e) {
        $('cfFallback')?.removeAttribute('hidden');
        window.__aeDiagnostic?.({ issue: 'manifest_failed', msg: String(e?.message || e).slice(0, 160) });
        return;
    }

    /* Deep link: ?c=de opens on Germany. The canonical tag already strips the
       query (layouts/app.blade.php), so this shares a link without minting 279
       indexable near-duplicates of one page. */
    const want = new URL(location.href).searchParams.get('c');
    if (want && manifest.byCode[want]) state.code = want;
    else if (window.__CF_DEFAULT && manifest.byCode[window.__CF_DEFAULT]) state.code = window.__CF_DEFAULT;

    buildColorChips();
    buildLayoutChips();
    buildPicker();
    renderCard(manifest.byCode[state.code]);

    fillSelect('cfFabric', Object.keys(FABRIC).map(k => [k, t('fabric.' + k, k)]), state.fabric);
    fillSelect('cfBackground', Object.keys(BACKGROUNDS).map(k => [k, t('bg.' + k, k)]), state.background);
    fillSelect('cfLighting', Object.keys(LIGHTING).map(k => [k, t('light.' + k, k)]), state.lighting);
    fillSelect('cfCamera', Object.keys(CAMERAS).map(k => [k, t('cam.' + k, k)]), state.camera);
    fillSelect('cfOutAspect', Object.keys(ASPECTS).map(k => [k, k]), state.outAspect);
    bind();

    setWhat($('cfWhat')?.value || 'video', { boot: true });
    applyDurationCap();
    ['tier-bootstrap:ready'].forEach(ev => {
        try { document.addEventListener(ev, applyDurationCap); } catch (_) {}
        try { window.addEventListener(ev, applyDurationCap); } catch (_) {}
    });
    stage('ready', { flags: manifest.flags.length });
}

/* ── Stage 2 · the studio, on demand ──────────────────────────────────────
   three.js is 670 KB and the cloth solver runs a physics loop; the two jobs
   most visitors arrive with — «чей это флаг» and «дайте картинку» — need
   neither. So the engine now boots the first time somebody actually asks for
   motion: the «На ветру» switch, the «Оживить» tab, any studio control, or an
   export. Idempotent and re-entrant — every caller awaits the same promise. */
let studioBoot = null;
function ensureStudio() {
    if (studioBoot) return studioBoot;
    const canvas = $('cfCanvas');
    if (!canvas || !manifest) return Promise.resolve(false);

    studioBoot = (async () => {
        try {
            const loadThree = () => (window.__assetChain
                ? window.__assetChain.importModule(THREE_URLS, 'three')
                /* asset-chain.js itself failed to load — walk the list by hand
                   rather than give up, since the first entry is our own origin. */
                : THREE_URLS.reduce((prev, url) => prev.catch(() => import(url)),
                    Promise.reject(new Error('start'))));
            THREE = await loadThree();
            scene = new FlagScene(THREE, canvas, {});
        } catch (e) {
            studioUnavailable(String(e?.message || e).slice(0, 160));
            return false;
        }
        await applyFlag();
        resizePreview();
        start();
        checkEncoder();
        window.__aeFunnel?.('studio_boot', { code: state.code });
        return true;
    })();
    return studioBoot;
}

/**
 * The animation half could not start. Say so where the animation is, leave the
 * catalogue alone, and turn off the buttons that would now fail — a disabled
 * control with a reason beside it beats one that throws when pressed.
 */
function studioUnavailable(msg) {
    $('cfFallback')?.removeAttribute('hidden');
    const st = $('cfStage');
    if (st) { st.setAttribute('hidden', ''); st.dataset.cfDead = '1'; }
    /* Back to the picture — the half that still works. */
    $('cfFlatWrap')?.removeAttribute('hidden');
    document.querySelectorAll('[data-cf-view]').forEach(b =>
        b.classList.toggle('is-on', b.dataset.cfView === 'flat'));
    document.querySelectorAll('[data-cf-export]').forEach(b => { b.disabled = true; });
    window.__aeDiagnostic?.({ issue: 'studio_unavailable', msg });
}


if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
